// Guided first day (spec 5, step 5): five quests per origin, each with a small reward.

import { TUNABLES } from "@degen/shared/config/tunables";
import type { Rng } from "./rng";
import { pushLog, type GameState } from "./state";
import { addTrust, type Origin } from "./player";
import type { Command } from "./engine";
import { queuePhishing } from "./events/random";

export type TutorialAction = "opened_chirp" | "read_trust" | "dismiss";

export interface TutorialState {
  track: Origin;
  step: number; // index into the track; >= length means done
  dismissed: boolean;
}

interface Quest {
  id: string;
  text: string;
  done(cmd: Command, state: GameState): boolean;
}

const isPost = (cmd: Command, t: string) => cmd.type === "POST" && cmd.postType === t;

export const QUESTS: Record<Origin, Quest[]> = {
  novice: [
    { id: "farm", text: "Walk to the Farm and start a testnet task", done: (c) => c.type === "FARM_TASK" && c.task === "testnet" },
    { id: "gm", text: "Open Chirp and post a gm", done: (c) => isPost(c, "gm") },
    { id: "follow", text: "Read an alpha post and follow the account", done: (c, s) => c.type === "FOLLOW" && s.social.following.includes(c.accountId) },
    { id: "dm", text: "A suspicious DM just arrived. Don't fall for it", done: (c) => c.type === "RESOLVE_EVENT" && c.eventId.startsWith("tut") },
    { id: "sleep", text: "Go Home and sleep to end the day", done: (c) => c.type === "SLEEP" },
  ],
  kol: [
    { id: "chirp", text: "Open Chirp and check your engagement", done: (c) => c.type === "TUTORIAL" && c.action === "opened_chirp" },
    { id: "buy", text: "Buy a small position in a trending coin on Pumpr", done: (c) => c.type === "BUY_TOKEN" },
    { id: "shill", text: "Shill that bag on Chirp and watch the price react", done: (c) => isPost(c, "shill") },
    { id: "trust", text: "Read the Trust tooltip", done: (c) => c.type === "TUTORIAL" && c.action === "read_trust" },
    { id: "sleep", text: "Go Home and sleep to end the day", done: (c) => c.type === "SLEEP" },
  ],
};

export function createTutorial(origin: Origin): TutorialState {
  return { track: origin, step: 0, dismissed: false };
}

export function currentQuest(t: TutorialState): Quest | null {
  if (t.dismissed) return null;
  return QUESTS[t.track][t.step] ?? null;
}

/** Called after every successful command. */
export function tutorialOnCommand(state: GameState, rng: Rng, cmd: Command) {
  const t = state.player.tutorial;
  if (cmd.type === "TUTORIAL" && cmd.action === "dismiss") {
    t.dismissed = true;
    return;
  }
  const q = currentQuest(t);
  if (!q || !q.done(cmd, state)) return;
  const p = state.player;
  // The scam quest pays only if you didn't click.
  const clicked = cmd.type === "RESOLVE_EVENT" && cmd.choice === "click";
  if (!clicked) {
    const r = TUNABLES.tutorial[t.track];
    p.cash += r.cash;
    p.dgn += TUNABLES.gas.questReward;
    p.followers += r.followers;
    addTrust(p, r.trust);
  }
  t.step++;
  const next = currentQuest(t);
  pushLog(state, "quest", next ? `✅ Quest done. Next: ${next.text}` : "🎓 Tutorial complete. You're on your own now, anon.");
  // The novice phishing lesson: a scripted (harmless) scam DM.
  if (next?.id === "dm") {
    const ev = queuePhishing(state, rng, true);
    ev.id = `tut-${ev.id}`;
  }
}
